<script setup lang="ts">
import SubchargeCircle from './SubchargeCircle.vue'
import { computed } from 'vue'
import { useNavigation } from './useNavigation'
import type { Student2Context } from '@/types/student2'

const MAX_SECONDS = 36 * 60 * 60 // 36 hours

const props = defineProps<{ context: Student2Context; subchargeDisabled?: boolean; chargingNow?: boolean }>()
const { navigationState, getLink, gotoOnClick } = useNavigation()

const percent = computed(() => (props.context.smartbook.seconds_to_discharge / MAX_SECONDS) * 100)
const trackColor = computed(() => {
  if (percent.value >= 75) return 'green'
  if (percent.value >= 25) return 'orange'
  return 'red'
})
const lastUnlockedUnit = computed(() => {
  const units = props.context.course_part.units
  const i = units.findIndex(e => e.id === props.context.smartbook.last_unit_unlocked_by_progress)
  return units[i >= 0 ? i : 0]
})
</script>

<template>
  <div class="student-mainpage-charge-bar">
    <div class="student-mainpage-charge-bar__charge" role="progressbar" :aria-valuenow="percent">
      <div
        class="student-mainpage-charge-bar__track"
        :class="`student-mainpage-charge-bar__track_color_${trackColor}`"
        :style="{ width: `calc(10px + (${percent}% - 10px))` }"
      />
    </div>

    <v-menu-base pos-horizontal="right" no-mobile-modal>
      <template #activator="{ onclick }">
        <!-- the circle shouldn't reach 100% without charging the battery -->
        <SubchargeCircle
          v-if="!context.smartbook.infinite_charge || subchargeDisabled"
          :percent="context.smartbook.subcharge_percent * 0.97"
          :subcharge-disabled="subchargeDisabled"
          :charging-now="chargingNow"
          class="ml-2"
          :style="subchargeDisabled ? 'cursor: pointer' : 'pointer-events: none'"
          @click="onclick"
        />
      </template>

      <div class="pa-2" style="width: 300px; white-space: normal">
        Сейчас вы обучаетесь на <b>{{ lastUnlockedUnit.title }}</b
        >, чтобы зарядить батарейку, прослушайте аудиоурок в <b>{{ lastUnlockedUnit.title }}</b>

        <v-button
          class="mt-1"
          mode="primary filled small"
          :href="getLink({ href: navigationState.currentLocation, unitId: lastUnlockedUnit.id })"
          @click="gotoOnClick"
        >
          Open {{ lastUnlockedUnit.title }}
        </v-button>
      </div>
    </v-menu-base>
  </div>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

.student-mainpage-charge-bar {
  display: flex;
  align-items: center;
  height: 48px;

  @include s2.mobile {
    height: 24px;
  }

  &__charge {
    flex: 1;

    height: 26px;
    border: 2px solid #191919;
    border-radius: 6px;
    background: $lightenOrange;

    margin-right: 5px;
    position: relative;

    &::after {
      content: ' ';
      border-radius: 2px;
      height: 16px;
      width: 7px;
      background: #191919;
      position: absolute;
      right: -7px;
      top: 3px;
    }

    @include s2.mobile {
      height: 24px;
      border-width: 1px;
      border-radius: 4px;
      &::after {
        height: 16px;
        width: 7px;
      }
    }
  }

  &__track {
    min-width: 10px;
    height: 100%;
    border-radius: 4px;
    max-width: 100%;

    &_color {
      &_red {
        background: $red;
      }
      &_orange {
        background: $orange;
      }
      &_green {
        background: $green;
      }
    }
  }
}
</style>
